import { EAS_PROFILE_NAME_RULE, isEasProfileName } from '@hangar/shared';
import { EXECUTION_MODE_LABEL } from '@/components/badges';
import { allowedModesFor, isBitrise, type ProfileFormValues, type ProjectEnvironment } from './profileFormModel';
import { submitError } from './bindingSubmitModel';

/** Local (client-side) validation of the build-profile form. Messages are shown under the field that owns them. */

export const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;
/** A Bitrise workflow id as bitrise.yml names it (`android-apk`, `deploy_release`). */
export const WORKFLOW_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,99}$/;

export const LOCAL_FIELDS = ['name', 'slug', 'providerAccountId', 'executionModes', 'providerProfile', 'bindings', 'timeoutMinutes', 'launchForm', 'sizeBudgetMb'] as const;
export type LocalField = (typeof LOCAL_FIELDS)[number];
export type LocalErrors = Record<LocalField, string | undefined>;

/** What validation needs besides the values: the kind of the picked account and the environments being bound. */
export interface ValidationContext {
  accountKind: string | undefined;
  environments: ProjectEnvironment[];
}

/** An EAS profile name must follow the API's rule; empty is fine (the profile above is used). */
export const easNameError = (value: string): string | undefined => (value.trim() && !isEasProfileName(value.trim()) ? `eas.json profile names: ${EAS_PROFILE_NAME_RULE}` : undefined);

/** A Bitrise workflow is required for every bound environment and must look like a bitrise.yml workflow id. */
export function workflowError(value: string): string | undefined {
  if (!value.trim()) return 'The build workflow this environment builds with';
  return WORKFLOW_RE.test(value.trim()) ? undefined : 'Letters, digits, ".", "_" and "-"';
}

/** The launch form is optional; when present it must be a JSON object. */
export function launchFormValid(json: string): boolean {
  if (!json.trim()) return true;
  try {
    return typeof JSON.parse(json) === 'object';
  } catch {
    return false;
  }
}

/** Per-environment error: only bound environments are checked, against the rule of the provider, then their submit settings. */
export function bindingErrorsOf(values: ProfileFormValues, ctx: ValidationContext): Record<string, string | undefined> {
  const check = isBitrise(ctx.accountKind) ? workflowError : easNameError;
  return Object.fromEntries(ctx.environments.map((e) => {
    const b = values.bindings[e.id];
    if (!b?.on) return [e.id, undefined];
    return [e.id, check(b.profile) ?? (b.storeAppId && b.submit ? submitError(b.submit) : undefined)];
  }));
}

const modesError = (values: ProfileFormValues, accountKind: string | undefined): string | undefined => {
  const allowed = allowedModesFor(accountKind);
  if (values.modes.some((m) => allowed.includes(m))) return undefined;
  return `Pick at least one mode (${EXECUTION_MODE_LABEL[isBitrise(accountKind) ? 'provider_api' : 'hangar_runner']})`;
};

const timeoutError = (timeout: string): string | undefined => (!/^\d+$/.test(timeout) || Number(timeout) < 5 || Number(timeout) > 720 ? '5–720 minutes' : undefined);

export interface FormValidation {
  local: LocalErrors;
  bindingErrors: Record<string, string | undefined>;
  invalid: boolean;
}

/** Validates the whole form: name, slug, account, modes, EAS profile, bindings, timeout 5..720, launch form, size budget. */
export function validateProfileForm(values: ProfileFormValues, ctx: ValidationContext): FormValidation {
  const bindingErrors = bindingErrorsOf(values, ctx);
  const local: LocalErrors = {
    name: !values.name.trim() ? 'Name is required' : undefined,
    slug: !SLUG_RE.test(values.slug) ? 'Lowercase letters, digits and hyphens' : undefined,
    providerAccountId: !values.providerAccountId ? 'Pick a provider account' : undefined,
    executionModes: modesError(values, ctx.accountKind),
    providerProfile: isBitrise(ctx.accountKind) ? undefined : easNameError(values.providerProfile),
    bindings: Object.values(bindingErrors).find(Boolean),
    timeoutMinutes: timeoutError(values.timeout),
    launchForm: launchFormValid(values.launchFormJson) ? undefined : 'Not valid JSON',
    sizeBudgetMb: values.sizeBudgetMb && !/^\d+$/.test(values.sizeBudgetMb) ? 'Whole megabytes' : undefined,
  };
  return { local, bindingErrors, invalid: Object.values(local).some(Boolean) };
}
